/**
 * Outlook sending-identity predicates.
 *
 * In lawyer-identity mode Outlook sends matter email from the mailbox of the
 * matter's ACTING SOLICITOR — never from whoever clicks Send. A paralegal
 * sending for a lawyer is normal and correct, so "the send lands in the
 * sender's own Sent Items" is only true when the person sending happens to be
 * the acting solicitor. These helpers answer that one question so every send
 * surface gates on the same rule instead of on "is Outlook active".
 */

import type { OutlookIdentityMode } from './types';

/**
 * Case-insensitive email comparison. Graph, Keycloak, and our own records
 * disagree on casing for the same mailbox, so never compare raw.
 */
export function emailsMatch(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  if (!a || !b) return false;
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

export interface OutlookSelfSendParams {
  /** Outlook is flag-enabled AND active for the firm. */
  outlookActive: boolean;
  /** The firm's configured send-identity mode; null when Outlook isn't active. */
  identityMode: OutlookIdentityMode | null;
  /** Email of the person composing / clicking Send. */
  currentUserEmail: string | null | undefined;
  /** Email of the matter's acting solicitor — the lawyer-mode sending identity. */
  actingSolicitorEmail: string | null | undefined;
}

/**
 * True when the acting user IS the identity Outlook will send from, so the
 * message really does land in their own Sent Items and a BCC-self copy would
 * be redundant.
 *
 * False — i.e. keep the "Send me a copy" affordance — in every other case:
 *
 * - **SES transport.** Nothing changed for these firms.
 * - **`shared` identity mode.** The sender is a firm mailbox, not a person; the
 *   send lands in nobody's personal Sent Items.
 * - **Someone else is the acting solicitor.** The paralegal case: the message
 *   goes out of the lawyer's mailbox and appears in the lawyer's Sent Items, so
 *   suppressing the self-copy leaves the sender with no copy in any mailbox
 *   they can see — a regression versus the old SES path.
 */
export function outlookSendsFromCurrentUser({
  outlookActive,
  identityMode,
  currentUserEmail,
  actingSolicitorEmail
}: OutlookSelfSendParams): boolean {
  if (!outlookActive) return false;
  if (identityMode !== 'lawyer') return false;
  return emailsMatch(currentUserEmail, actingSolicitorEmail);
}
